import { MY_CLOUD_SPACE } from "@bcmi-labs/cloud-sidebar"
import { queryOptions } from "@tanstack/react-query"

import { getSpaceKits, getSpacesClean, getSpacesInviteCode, getUsersList } from "@/api/classroom"
import { getKits } from "@/api/education"
import { $iotApiClient } from "@/api/iot"
import { authFetch, getXOrganizationHeaders } from "@/api/utils"
import { NetworkError, unwrapResponse } from "@/api/utils/NetworkError"
import { MY_SPACE } from "@/constants/spaces"
import { env } from "@/env"
import type { Space, Subscriptions, Template, TemplateCardType } from "@/types"

// TODO: Decide how we plan on supporting weird APIs like these.

/**
 * Centralized query functions
 * Every object exports the relative query function and query key.
 */
export const query = {
  /** @deprecated Deprecating away: use the $rq* clients instead. */
  trigger: {
    iotList: (id: Space["id"]) =>
      queryOptions({
        queryKey: [`iot/trigger/list/${id}`],
        queryFn: async ({ signal }) => {
          const res = await $iotApiClient.GET("/iot/v1/triggers", {
            signal,
            params: { header: getXOrganizationHeaders(id) }
          })
          if (res.error) throw new NetworkError(undefined, res.response)
        }
      })
  },
  space: {
    list: queryOptions({
      queryKey: [`spaces/list`],
      queryFn: async ({ client, queryKey }) => {
        try {
          return await getSpacesClean()
        } catch (error) {
          console.error(error)
          // Keep what we already have on a failed refetch; only an initial failure falls back to an empty list.
          return client.getQueryData<Awaited<ReturnType<typeof getSpacesClean>>>(queryKey) ?? []
        }
      }
    }),
    // TODO: Figure out a way to prevent errors from being thrown when an enterprise space is provided
    kits: (id: Space["id"]) =>
      queryOptions({
        queryKey: [`spaces/kits/${id}`],
        queryFn: () => (id === MY_CLOUD_SPACE.id ? getKits() : getSpaceKits(id))
      }),
    subscriptions: (id: Space["id"]) =>
      queryOptions({
        queryKey: [`spaces/subscriptions/${id}`],
        queryFn: async () => {
          const response = await authFetch(`${env.VITE_API2_URL}/restrictions/v1/recap/me`, {
            headers: getXOrganizationHeaders(id)
          })
          return unwrapResponse<Subscriptions>(response)
        }
      }),
    /** Retrieves the list of members for the given space. */
    members: (id: Space["id"]) =>
      queryOptions({
        queryKey: [`members/list/${id}`],
        queryFn: () => getUsersList(id)
      }),
    /** Retrieves the invite code for the given space. */
    inviteCode: (id: Space["id"]) =>
      queryOptions({
        queryKey: [`space/code/${id}`],
        queryFn: () => getSpacesInviteCode(id)
      })
  },
  user: {
    /** Retrieves the list of subscriptions for the current user. */
    subscriptions: queryOptions({
      queryKey: [`subscriptions`],
      queryFn: async () => {
        const response = await authFetch(`${env.VITE_API2_URL}/restrictions/v1/recap/me`, {
          headers: getXOrganizationHeaders(MY_SPACE.id)
        })
        return unwrapResponse<Subscriptions>(response)
      }
    })
  },
  // ! This endpoint does not have a schema.
  "iot-templates": {
    /** Retrieves the list of IoT templates. */
    list: queryOptions({
      queryKey: [`iot-templates/list`],
      queryFn: async () =>
        unwrapResponse<TemplateCardType[]>(
          await authFetch(`${env.VITE_TEMPLATE_URL}/templates.json`, {
            credentials: "include"
          })
        )
    }),
    /** Retrieves the requested IoT template. */
    single: (name: string) =>
      queryOptions({
        queryKey: [`iot-templates/single/${name}`],
        queryFn: async () =>
          unwrapResponse<Template>(
            await authFetch(`${env.VITE_TEMPLATE_URL}/templates/${name}/template.json`, {
              credentials: "include"
            })
          )
      })
  },
  other: {
    /** Retrieves the list of countries.
     * By default, this query will never be stale.
     */
    countries: queryOptions({
      queryKey: [`countries`],
      queryFn: async () => {
        try {
          const res = await fetch(`${env.VITE_API2_URL}/countries/v1`)
          const countries = await unwrapResponse<{ code: string; name: string }[]>(res)

          // Rname code and name to value and label
          return countries.map(({ code, name }) => ({ value: code, label: name }))
        } catch (error) {
          console.error(error)
          return []
        }
      },
      staleTime: Infinity
    })
  }
}
